import axios from 'axios';

import { resolverApiUrl } from './urls';

export const apiUrl = resolverApiUrl(window.location.hostname, window.location.origin, process.env.REACT_APP_API_URL);
console.log('[API] Using API URL:', apiUrl);

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include token
api.interceptors.request.use(
  config => {
    // FormData (uploads multipart): el default global 'application/json' pisa
    // el boundary y el server recibe cero archivos ("Ninguna imagen recibida").
    // Borrarlo deja que axios/el browser pongan multipart con boundary correcto.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    const token = localStorage.getItem('token');
    if (token) {
      // Asegurar que el header se establezca correctamente
      config.headers['x-access-token'] = token;
      // También agregar como Authorization header para mayor compatibilidad
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // Debug: Log para verificar que se está agregando el token
      // Debug logging removed
    } else {
      // Debug removed
    }
    return config;
  },
  error => {
    console.error('[API Interceptor] Request error:', error);
    return Promise.reject(error);
  }
);

// Reintento silencioso para cortes de red breves: sólo GET, sólo cuando el
// error no tiene response (ERR_NETWORK/timeout, no un 4xx/5xx real del server).
// Evita que un blip de un segundo de wifi/4G llegue a mostrarse como error.
const NETWORK_RETRY_DELAYS_MS = [300, 800];

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add a response interceptor
api.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    const isGet = config && (config.method || 'get').toLowerCase() === 'get';
    const isNetworkError = !error.response;

    if (config && isGet && isNetworkError && !config.__retriedForNetwork) {
      config.__retriedForNetwork = true;
      for (const delay of NETWORK_RETRY_DELAYS_MS) {
        await sleep(delay);
        try {
          return await api(config);
        } catch (retryErr) {
          error = retryErr;
          if (error.response) break; // ya no es un error de red, no seguir reintentando
        }
      }
    }

    // Save error to localStorage for debugging
    const errorInfo = {
      timestamp: new Date().toISOString(),
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      responseData: error.response?.data
    };
    
    // Debug removed
    
    // Store last API error
    localStorage.setItem('lastApiError', JSON.stringify(errorInfo));
    
    // Handle 401 error (unauthorized)
    if (error.response && error.response.status === 401) {
      // Don't logout if we're already on the login page
      if (window.location.pathname === '/login' || window.location.pathname === '/admin/login') {
        return Promise.reject(error);
      }
      
      // Don't logout for certain endpoints that might fail during initial load
      const safeEndpoints = [
        '/api/conversations/search', 
        '/api/dashboard/stats',
        '/api/dashboard/kpis',
        '/api/analytics/data',
        '/api/tags',
        '/api/conversations',  // Agregar este endpoint que puede fallar al inicio
        '/api/wati/config',
        '/api/wati/dashboard',
        '/api/wati/real-time-metrics'
      ];
      if (error.config?.url && safeEndpoints.some(endpoint => error.config.url.includes(endpoint))) {
        // Debug removed
        return Promise.reject(error);
      }
      
      // Debug removed
      localStorage.setItem('logoutReason', '401 Unauthorized from ' + error.config?.url);
      // Clear tokens and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;